import { Component, ChangeDetectionStrategy, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Champ mot de passe avec bascule d'affichage.
 * Implémente ControlValueAccessor : compatible formControlName / ngModel.
 */
@Component({
  selector: 'idra-password',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IdraPassword), multi: true },
  ],
  styles: `
    :host {
      display: block;
    }

    .wrap {
      position: relative;
    }

    input {
      padding-right: 40px;
    }

    .toggle {
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--idra-text-muted);
      border-radius: var(--idra-radius-sm);
    }

    .toggle:hover {
      color: var(--idra-text);
    }

    .toggle:focus-visible {
      outline: none;
      box-shadow: var(--idra-focus-ring);
    }
  `,
  template: `
    <div class="wrap">
      <input
        class="idra-input w-full"
        [id]="inputId()"
        [type]="masked() ? 'password' : 'text'"
        [value]="value()"
        [disabled]="disabled()"
        [attr.placeholder]="placeholder() || null"
        [attr.autocomplete]="autocomplete()"
        (input)="onInput($event)"
        (blur)="onTouched()"
      />
      @if (toggleMask()) {
        <button
          type="button"
          class="toggle"
          [attr.aria-label]="masked() ? 'Afficher le mot de passe' : 'Masquer le mot de passe'"
          (click)="masked.set(!masked())"
        >
          <i [class]="masked() ? 'pi pi-eye' : 'pi pi-eye-slash'" aria-hidden="true"></i>
        </button>
      }
    </div>
  `,
})
export class IdraPassword implements ControlValueAccessor {
  readonly inputId = input<string | undefined>(undefined);
  readonly toggleMask = input(true);
  readonly autocomplete = input('current-password');
  readonly placeholder = input('');

  protected readonly value = signal('');
  protected readonly disabled = signal(false);
  protected readonly masked = signal(true);

  protected onChange: (value: string) => void = () => {};
  protected onTouched: () => void = () => {};

  protected onInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
  }

  /** Affiche le mot de passe en clair (utile après une génération). */
  reveal(): void {
    this.masked.set(false);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
